import { supabase, Movimiento } from '../lib/supabase';
import { balanceService, BalanceResumen, EvolucionMensualBalance } from './balanceService';
import type { OwnerFondoCampana, SaldoRestringidoCampana } from '../types/fondosCampana';
import { calcularTotales, consolidarTotales, type MovimientoMonto } from '../utils/balanceCalculations';
import { fechaInicioMes, fechaFinMes } from '../utils/dateHelpers';
import { SELECT_MOVIMIENTO_CON_COMPROBANTES } from './movimientoService';

const TABLA_NOTIFICACIONES = 'notificaciones_balance';
const SELECT_ID_NOMBRE = 'id, nombre';

export interface BalanceEntidad {
  entidad_id: string;
  entidad_nombre: string;
  ingresos: number;
  egresos: number;
  /** Entradas por financiamiento de deudas: fuera de los ingresos, dentro del balance. */
  financiamiento: number;
  balance: number;
  ha_notificado_balance: boolean;
}

export interface BalanceConsolidado {
  total_ingresos: number;
  total_egresos: number;
  total_financiamiento: number;
  balance_neto: number;
  entidades_notificadas: number;
  total_entidades: number;
  entidades: BalanceEntidad[];
}

export interface DetalleMovimientos {
  ingresos: Movimiento[];
  egresos: Movimiento[];
  total_ingresos: number;
  total_egresos: number;
  total_financiamiento: number;
  balance_neto: number;
}

/**
 * Función auxiliar para obtener movimientos de una entidad (lumisial o diócesis) en un período.
 * Centraliza la lógica de consulta que se repite en múltiples métodos.
 */
async function obtenerMovimientosEntidad(
  entidadId: string,
  tipoEntidad: 'lumisial' | 'diocesis',
  fechaInicio: string,
  fechaFin: string,
  filtros?: {
    afectaBalance?: boolean;
    ordenar?: boolean;
  }
): Promise<Movimiento[]> {
  if (tipoEntidad === 'lumisial') {
    return obtenerMovimientosLumisial(entidadId, fechaInicio, fechaFin, filtros);
  } else {
    return obtenerMovimientosDiocesis(entidadId, fechaInicio, fechaFin, filtros);
  }
}

/**
 * Obtener movimientos de un lumisial específico.
 */
async function obtenerMovimientosLumisial(
  lumisialId: string,
  fechaInicio: string,
  fechaFin: string,
  filtros?: {
    afectaBalance?: boolean;
    ordenar?: boolean;
  }
): Promise<Movimiento[]> {
  let query = supabase
    .from('movimientos')
    .select(SELECT_MOVIMIENTO_CON_COMPROBANTES)
    .eq('lumisial_id', lumisialId)
    .gte('fecha', fechaInicio)
    .lte('fecha', fechaFin)
    .eq('anulado', false);

  query = aplicarFiltrosMovimientos(query, filtros);

  const { data, error } = await query;
  if (error) throw error;
  // La constante de select no es un literal: supabase-js no tipa el resultado (ver movimientoService).
  return (data ?? []) as unknown as Movimiento[];
}

/**
 * Obtener movimientos de todos los lumisiales de una diócesis.
 */
async function obtenerMovimientosDiocesis(
  diocesisId: string,
  fechaInicio: string,
  fechaFin: string,
  filtros?: {
    afectaBalance?: boolean;
    ordenar?: boolean;
  }
): Promise<Movimiento[]> {
  const { data: lumisiales, error: lumisialesError } = await supabase
    .from('lumisiales')
    .select('id')
    .eq('diocesis_id', diocesisId)
    .eq('activo', true);

  if (lumisialesError) throw lumisialesError;

  const lumisialIds = (lumisiales || []).map(l => l.id);

  if (lumisialIds.length === 0) {
    return [];
  }

  let query = supabase
    .from('movimientos')
    .select(SELECT_MOVIMIENTO_CON_COMPROBANTES)
    .in('lumisial_id', lumisialIds)
    .gte('fecha', fechaInicio)
    .lte('fecha', fechaFin)
    .eq('anulado', false);

  query = aplicarFiltrosMovimientos(query, filtros);

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as unknown as Movimiento[];
}

/**
 * Obtiene los IDs de lumisiales activos de una entidad (diócesis o provincia).
 */
async function obtenerLumisialIdsPorEntidad(
  entidadId: string,
  tipoEntidad: 'diocesis' | 'provincia'
): Promise<string[]> {
  if (tipoEntidad === 'diocesis') {
    const { data, error } = await supabase
      .from('lumisiales')
      .select('id')
      .eq('diocesis_id', entidadId)
      .eq('activo', true);
    if (error) throw error;
    return (data || []).map(l => l.id);
  }

  const { data: diocesis, error: dError } = await supabase
    .from('diocesis')
    .select('id')
    .eq('provincia_eclesiastica_id', entidadId)
    .eq('activo', true);
  if (dError) throw dError;

  const diocesisIds = (diocesis || []).map(d => d.id);
  if (diocesisIds.length === 0) return [];

  const { data: lumisiales, error: lError } = await supabase
    .from('lumisiales')
    .select('id')
    .in('diocesis_id', diocesisIds)
    .eq('activo', true);
  if (lError) throw lError;
  return (lumisiales || []).map(l => l.id);
}

/**
 * Aplica filtros opcionales a una query de movimientos.
 */
function aplicarFiltrosMovimientos<Q extends {
  eq(column: string, value: boolean): Q;
  order(column: string, options: { ascending: boolean }): Q;
}>(
  query: Q,
  filtros?: { afectaBalance?: boolean; ordenar?: boolean }
): Q {
  const { afectaBalance, ordenar = false } = filtros || {};
  let result: Q = query;

  if (afectaBalance !== undefined) {
    result = result.eq('afecta_balance', afectaBalance);
  }

  if (ordenar) {
    result = result.order('fecha', { ascending: false });
  }

  return result;
}

/**
 * Calcula balance a partir de movimientos de tipo ingreso/egreso.
 */
function calcularBalanceResumen(movimientos: readonly MovimientoMonto[] | null): BalanceResumen {
  const { ingresos, egresos, financiamiento, balance } = calcularTotales(movimientos ?? []);
  return {
    ingresos,
    egresos,
    financiamiento,
    balance,
    cantidadMovimientos: movimientos?.length ?? 0,
    ingresosDesglosados: { aportesMensualesMiembros: 0, cuotaIglesiaRecaudada: 0, cuotaIglesiaJal: 0, cuotaIglesiaPA: 0, aportesExternos: 0, actividadesEconomicas: 0, campanas: 0 },
    egresosDesglosados: { actividades: 0, gastosGenerales: 0, gastosLiturgicos: 0, gastosMisionales: 0, otros: 0 },
  };
}

/**
 * Consulta movimientos de JA (JAD o JAN) para un período sin lumisial.
 */
async function consultarBalanceJA(
  tipo: 'jad' | 'jan',
  id: string,
  mes: number,
  anio: number
): Promise<BalanceResumen> {
  const fechaInicio = fechaInicioMes(mes, anio);
  const fechaFin = fechaFinMes(mes, anio);
  const baseQuery = supabase.from('movimientos').select('tipo, monto, origen_tipo');
  const withFilter = tipo === 'jad'
    ? baseQuery.eq('diocesis_id', id)
    : baseQuery.eq('provincia_eclesiastica_id', id);
  const { data, error } = await withFilter
    .is('lumisial_id', null)
    .eq('afecta_balance', true)
    .eq('anulado', false)
    .gte('fecha', fechaInicio)
    .lte('fecha', fechaFin);
  if (error) throw error;
  return calcularBalanceResumen(data);
}

export const balanceConsolidadoService = {
  /** Fondos de la entidad elegida; separados de la caja libre consolidada. */
  async getSaldosRestringidosCampana(owner: OwnerFondoCampana): Promise<SaldoRestringidoCampana[]> {
    return balanceService.getSaldosRestringidosCampana(owner);
  },

  /**
   * Obtener balance consolidado de lumisiales de una diócesis.
   * Solo considera movimientos con afecta_balance = true.
   */
  async getBalanceDiocesano(
    diocesisId: string,
    mes: number,
    anio: number
  ): Promise<BalanceConsolidado> {
    const fechaInicio = fechaInicioMes(mes, anio);
    const fechaFin = fechaFinMes(mes, anio);

    // Obtener lumisiales de la diócesis
    const { data: lumisiales, error: lumisialesError } = await supabase
      .from('lumisiales')
      .select(SELECT_ID_NOMBRE)
      .eq('diocesis_id', diocesisId)
      .eq('activo', true);

    if (lumisialesError) throw lumisialesError;

    if (!lumisiales || lumisiales.length === 0) {
      return {
        total_ingresos: 0,
        total_egresos: 0,
        total_financiamiento: 0,
        balance_neto: 0,
        entidades_notificadas: 0,
        total_entidades: 0,
        entidades: [],
      };
    }

    const lumisialIds = lumisiales.map(l => l.id);

    // Obtener movimientos de todos los lumisiales
    const { data: movimientos, error: movimientosError } = await supabase
      .from('movimientos')
      .select('*')
      .in('lumisial_id', lumisialIds)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin)
      .eq('afecta_balance', true)
      .eq('anulado', false);

    if (movimientosError) throw movimientosError;

    // Obtener qué lumisiales ya enviaron notificación de balance para este período
    const { data: notificaciones, error: notifError } = await supabase
      .from(TABLA_NOTIFICACIONES)
      .select('entidad_id')
      .in('entidad_id', lumisialIds)
      .eq('periodo_mes', mes)
      .eq('periodo_anio', anio);

    if (notifError) throw notifError;
    const lumisialesNotificados = new Set((notificaciones || []).map(n => n.entidad_id));

    // Agrupar por lumisial y calcular totales
    const entidades: BalanceEntidad[] = lumisiales.map(lumisial => {
      const movimientosLumisial = (movimientos || []).filter(m => m.lumisial_id === lumisial.id);
      const { ingresos, egresos, financiamiento, balance } = calcularTotales(movimientosLumisial);

      return {
        entidad_id: lumisial.id,
        entidad_nombre: lumisial.nombre,
        ingresos,
        egresos,
        financiamiento,
        balance,
        ha_notificado_balance: lumisialesNotificados.has(lumisial.id),
      };
    });

    // Calcular totales consolidados
    const { ingresos: total_ingresos, egresos: total_egresos, financiamiento: total_financiamiento, balance: balance_neto } =
      consolidarTotales(entidades);

    const entidades_notificadas = entidades.filter(e => e.ha_notificado_balance).length;

    return {
      total_ingresos,
      total_egresos,
      total_financiamiento,
      balance_neto,
      entidades_notificadas,
      total_entidades: entidades.length,
      entidades,
    };
  },

  /**
   * Obtener balance consolidado de diócesis de una provincia eclesiástica.
   * Solo considera movimientos con afecta_balance = true.
   */
  async getBalanceNacional(
    provinciaEclesiasticaId: string,
    mes: number,
    anio: number
  ): Promise<BalanceConsolidado> {
    const fechaInicio = fechaInicioMes(mes, anio);
    const fechaFin = fechaFinMes(mes, anio);

    // Obtener diócesis de la provincia
    const { data: diocesis, error: diocesisError } = await supabase
      .from('diocesis')
      .select(SELECT_ID_NOMBRE)
      .eq('provincia_eclesiastica_id', provinciaEclesiasticaId)
      .eq('activo', true);

    if (diocesisError) throw diocesisError;

    if (!diocesis || diocesis.length === 0) {
      return {
        total_ingresos: 0,
        total_egresos: 0,
        total_financiamiento: 0,
        balance_neto: 0,
        entidades_notificadas: 0,
        total_entidades: 0,
        entidades: [],
      };
    }

    const diocesisIds = diocesis.map(d => d.id);

    // Obtener todos los lumisiales de estas diócesis
    const { data: lumisiales, error: lumisialesError } = await supabase
      .from('lumisiales')
      .select('id, diocesis_id')
      .in('diocesis_id', diocesisIds)
      .eq('activo', true);

    if (lumisialesError) throw lumisialesError;

    const lumisialIds = (lumisiales || []).map(l => l.id);

    if (lumisialIds.length === 0) {
      return {
        total_ingresos: 0,
        total_egresos: 0,
        total_financiamiento: 0,
        balance_neto: 0,
        entidades_notificadas: 0,
        total_entidades: diocesis.length,
        entidades: diocesis.map(d => ({
          entidad_id: d.id,
          entidad_nombre: d.nombre,
          ingresos: 0,
          egresos: 0,
          financiamiento: 0,
          balance: 0,
          ha_notificado_balance: false,
        })),
      };
    }

    // Obtener movimientos de todos los lumisiales
    const { data: movimientos, error: movimientosError } = await supabase
      .from('movimientos')
      .select('*')
      .in('lumisial_id', lumisialIds)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin)
      .eq('afecta_balance', true)
      .eq('anulado', false);

    if (movimientosError) throw movimientosError;

    // Obtener qué diócesis ya enviaron notificación de balance para este período
    const { data: notificaciones, error: notifError } = await supabase
      .from(TABLA_NOTIFICACIONES)
      .select('entidad_id')
      .in('entidad_id', diocesisIds)
      .eq('periodo_mes', mes)
      .eq('periodo_anio', anio);

    if (notifError) throw notifError;
    const diocesisNotificadas = new Set((notificaciones || []).map(n => n.entidad_id));

    // Agrupar por diócesis y calcular totales
    const entidades: BalanceEntidad[] = diocesis.map(diocesisItem => {
      const lumisialesDiocesis = new Set(
        (lumisiales || [])
          .filter(l => l.diocesis_id === diocesisItem.id)
          .map(l => l.id)
      );

      const movimientosDiocesis = (movimientos || []).filter(m =>
        lumisialesDiocesis.has(m.lumisial_id)
      );
      const { ingresos, egresos, financiamiento, balance } = calcularTotales(movimientosDiocesis);

      return {
        entidad_id: diocesisItem.id,
        entidad_nombre: diocesisItem.nombre,
        ingresos,
        egresos,
        financiamiento,
        balance,
        ha_notificado_balance: diocesisNotificadas.has(diocesisItem.id),
      };
    });

    // Calcular totales consolidados
    const { ingresos: total_ingresos, egresos: total_egresos, financiamiento: total_financiamiento, balance: balance_neto } =
      consolidarTotales(entidades);

    const entidades_notificadas = entidades.filter(e => e.ha_notificado_balance).length;

    return {
      total_ingresos,
      total_egresos,
      total_financiamiento,
      balance_neto,
      entidades_notificadas,
      total_entidades: entidades.length,
      entidades,
    };
  },

  /**
   * Obtener desglose detallado de una entidad específica.
   * Solo considera movimientos con afecta_balance = true.
   */
  async getDesgloseEntidad(
    entidadId: string,
    tipoEntidad: 'lumisial' | 'diocesis',
    mes: number,
    anio: number
  ): Promise<DetalleMovimientos> {
    const fechaInicio = fechaInicioMes(mes, anio);
    const fechaFin = fechaFinMes(mes, anio);

    const movimientos = await obtenerMovimientosEntidad(
      entidadId,
      tipoEntidad,
      fechaInicio,
      fechaFin,
      { afectaBalance: true, ordenar: true }
    );

    const ingresos = movimientos.filter(m => m.tipo === 'ingreso');
    const egresos = movimientos.filter(m => m.tipo === 'egreso');

    const totales = calcularTotales(movimientos);

    return {
      ingresos,
      egresos,
      total_ingresos: totales.ingresos,
      total_egresos: totales.egresos,
      total_financiamiento: totales.financiamiento,
      balance_neto: totales.balance,
    };
  },

  /**
   * Obtener evolución anual (12 meses) del balance consolidado de una entidad.
   * Para 'diocesis': agrupa los lumisiales de esa diócesis.
   * Para 'provincia': agrupa todos los lumisiales de esa provincia.
   */
  async getEvolucionAnualConsolidada(
    entidadId: string,
    tipoEntidad: 'diocesis' | 'provincia',
    año: number
  ): Promise<EvolucionMensualBalance[]> {
    const fechaInicio = `${año}-01-01`;
    const fechaFin = `${año}-12-31`;

    const lumisialIds = await obtenerLumisialIdsPorEntidad(entidadId, tipoEntidad);

    if (lumisialIds.length === 0) {
      return Array.from({ length: 12 }, (_, i) => ({
        mes: i + 1,
        ingresos: 0,
        egresos: 0,
        financiamiento: 0,
        balance: 0,
      }));
    }

    const { data: movimientos, error: movError } = await supabase
      .from('movimientos')
      .select('fecha, tipo, monto, afecta_balance, origen_tipo')
      .in('lumisial_id', lumisialIds)
      .eq('anulado', false)
      .eq('afecta_balance', true)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin);

    if (movError) throw movError;

    const porMes = new Map<number, MovimientoMonto[]>();
    for (const mov of movimientos || []) {
      const mes = new Date(mov.fecha + 'T00:00:00').getMonth() + 1;
      porMes.set(mes, [...(porMes.get(mes) ?? []), mov]);
    }

    return Array.from({ length: 12 }, (_, i) => {
      const mes = i + 1;
      const { ingresos, egresos, financiamiento, balance } = calcularTotales(porMes.get(mes) ?? []);
      return { mes, ingresos, egresos, financiamiento, balance };
    });
  },

  /**
   * Obtener balance de la JAD (Junta Administrativa Diocesana) para un período.
   * Solo considera movimientos con diocesis_id = diocesisId, lumisial_id IS NULL y afecta_balance = true.
   */
  async getBalanceJAD(diocesisId: string, mes: number, anio: number): Promise<BalanceResumen> {
    return consultarBalanceJA('jad', diocesisId, mes, anio);
  },

  /**
   * Obtener balance de la JAN (Junta Administrativa Nacional) para un período.
   * Solo considera movimientos con provincia_eclesiastica_id = provinciaId, lumisial_id IS NULL y afecta_balance = true.
   */
  async getBalanceJAN(provinciaId: string, mes: number, anio: number): Promise<BalanceResumen> {
    return consultarBalanceJA('jan', provinciaId, mes, anio);
  },

  /**
   * Obtener balance de la JAD por cada diócesis de una provincia, para un período.
   * Una entrada por diócesis con los totales de sus movimientos JAD.
   */
  async getBalanceJADsPorProvincia(
    provinciaId: string,
    mes: number,
    anio: number
  ): Promise<BalanceEntidad[]> {
    const fechaInicio = fechaInicioMes(mes, anio);
    const fechaFin = fechaFinMes(mes, anio);

    const { data: diocesis, error: diocesisError } = await supabase
      .from('diocesis')
      .select(SELECT_ID_NOMBRE)
      .eq('provincia_eclesiastica_id', provinciaId)
      .eq('activo', true);

    if (diocesisError) throw diocesisError;

    if (!diocesis || diocesis.length === 0) {
      return [];
    }

    const diocesisIds = diocesis.map(d => d.id);

    const { data: movimientos, error: movimientosError } = await supabase
      .from('movimientos')
      .select('tipo, monto, origen_tipo, diocesis_id')
      .in('diocesis_id', diocesisIds)
      .is('lumisial_id', null)
      .eq('afecta_balance', true)
      .eq('anulado', false)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin);

    if (movimientosError) throw movimientosError;

    // Obtener qué diócesis ya enviaron notificación de balance a JAN para este período
    const { data: notificaciones, error: notifError } = await supabase
      .from(TABLA_NOTIFICACIONES)
      .select('entidad_id')
      .in('entidad_id', diocesisIds)
      .eq('periodo_mes', mes)
      .eq('periodo_anio', anio);

    if (notifError) throw notifError;
    const diocesisNotificadas = new Set((notificaciones || []).map(n => n.entidad_id));

    return diocesis.map(diocesisItem => {
      const movimientosDiocesis = (movimientos || []).filter(
        m => m.diocesis_id === diocesisItem.id
      );
      const { ingresos, egresos, financiamiento, balance } = calcularTotales(movimientosDiocesis);

      return {
        entidad_id: diocesisItem.id,
        entidad_nombre: diocesisItem.nombre,
        ingresos,
        egresos,
        financiamiento,
        balance,
        ha_notificado_balance: diocesisNotificadas.has(diocesisItem.id),
      };
    });
  },

  /**
   * Obtener balance de la JAN por cada provincia, para un período.
   * Una entrada por provincia con los totales de sus movimientos JAN.
   */
  async getBalanceJANs(
    mes: number,
    anio: number
  ): Promise<BalanceEntidad[]> {
    const fechaInicio = fechaInicioMes(mes, anio);
    const fechaFin = fechaFinMes(mes, anio);

    const { data: provincias, error: provinciasError } = await supabase
      .from('provincias_eclesiasticas')
      .select(SELECT_ID_NOMBRE)
      .eq('activo', true);

    if (provinciasError) throw provinciasError;

    if (!provincias || provincias.length === 0) {
      return [];
    }

    const provinciaIds = provincias.map(p => p.id);

    const { data: movimientos, error: movimientosError } = await supabase
      .from('movimientos')
      .select('tipo, monto, origen_tipo, provincia_eclesiastica_id')
      .in('provincia_eclesiastica_id', provinciaIds)
      .is('lumisial_id', null)
      .eq('afecta_balance', true)
      .eq('anulado', false)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin);

    if (movimientosError) throw movimientosError;

    return provincias.map(provincia => {
      const movimientosProvincia = (movimientos || []).filter(
        m => m.provincia_eclesiastica_id === provincia.id
      );
      const { ingresos, egresos, financiamiento, balance } = calcularTotales(movimientosProvincia);

      return {
        entidad_id: provincia.id,
        entidad_nombre: provincia.nombre,
        ingresos,
        egresos,
        financiamiento,
        balance,
        ha_notificado_balance: false,
      };
    });
  },

  /**
   * Obtener TODOS los movimientos de una entidad (afecta_balance true y false).
   * Usado para vista de detalle de notificación de balance.
   */
  async getMovimientosCompletosEntidad(
    entidadId: string,
    tipoEntidad: 'lumisial' | 'diocesis',
    mes: number,
    anio: number
  ): Promise<{ movimientos_balance: Movimiento[]; movimientos_no_balance: Movimiento[] }> {
    const fechaInicio = fechaInicioMes(mes, anio);
    const fechaFin = fechaFinMes(mes, anio);

    const movimientos = await obtenerMovimientosEntidad(
      entidadId,
      tipoEntidad,
      fechaInicio,
      fechaFin,
      { ordenar: true }
    );

    const movimientos_balance = movimientos.filter(m => m.afecta_balance === true);
    const movimientos_no_balance = movimientos.filter(m => m.afecta_balance === false);

    return { movimientos_balance, movimientos_no_balance };
  },

  async getCuotaIglesiaDesgloseJAD(
    diocesisId: string,
    mes: number,
    anio: number
  ): Promise<{ lumisial_id: string; lumisial_nombre: string; monto: number; montoJAL: number; montoPA: number }[]> {
    const fechaInicio = fechaInicioMes(mes, anio);
    const fechaFin = fechaFinMes(mes, anio);

    // Los movimientos JAD tienen lumisial_id = null (son de la diócesis, no del lumisial).
    // El lumisial origen está en origen_id — no se puede usar JOIN por FK lumisial_id aquí.
    const { data, error } = await supabase
      .from('movimientos')
      .select('origen_id, monto, modo_pago_cuota_iglesia')
      .eq('diocesis_id', diocesisId)
      .eq('origen_tipo', 'lumisial')
      .eq('categoria_aporte', 'cuota_iglesia')
      .eq('tipo', 'ingreso')
      .eq('anulado', false)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin);

    if (error) throw error;
    if (!data || data.length === 0) return [];

    const lumisialIds = [...new Set(data.map(r => r.origen_id as string))];
    const { data: lumisialesData } = await supabase
      .from('lumisiales')
      .select(SELECT_ID_NOMBRE)
      .in('id', lumisialIds);

    const nombrePorId: Record<string, string> = {};
    for (const l of lumisialesData || []) nombrePorId[l.id] = l.nombre;

    // Agrupar por lumisial, separando JAL (modo null) y PA
    const porLumisial: Record<string, { montoJAL: number; montoPA: number }> = {};
    for (const row of data) {
      const id = row.origen_id as string;
      if (!porLumisial[id]) porLumisial[id] = { montoJAL: 0, montoPA: 0 };
      if (row.modo_pago_cuota_iglesia) {
        porLumisial[id].montoPA += Number(row.monto);
      } else {
        porLumisial[id].montoJAL += Number(row.monto);
      }
    }

    return lumisialIds.map(id => {
      const { montoJAL, montoPA } = porLumisial[id];
      return {
        lumisial_id: id,
        lumisial_nombre: nombrePorId[id] ?? '',
        monto: montoJAL + montoPA,
        montoJAL,
        montoPA,
      };
    });
  },

  async getCuotaIglesiaDesgloseJANPorDiocesis(
    provinciaId: string,
    mes: number,
    anio: number
  ): Promise<{ diocesis_id: string; diocesis_nombre: string; monto: number; n_lumisiales: number }[]> {
    const fechaInicio = fechaInicioMes(mes, anio);
    const fechaFin = fechaFinMes(mes, anio);

    // Los movimientos JAN solo tienen provincia_eclesiastica_id (sin diocesis_id por constraint owner_check),
    // así que no se puede usar el join diocesis!inner. Se resuelven los nombres por origen_id por separado.
    const { data, error } = await supabase
      .from('movimientos')
      .select('origen_id, monto')
      .eq('provincia_eclesiastica_id', provinciaId)
      .eq('origen_tipo', 'diocesis')
      .eq('categoria_aporte', 'cuota_iglesia')
      .eq('tipo', 'ingreso')
      .eq('anulado', false)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin);

    if (error) throw error;

    const rows = data || [];

    // Agrupar por diócesis (puede haber regular + PA como movimientos separados)
    const montosPorDiocesis: Record<string, number> = {};
    for (const row of rows) {
      const id = row.origen_id as string;
      montosPorDiocesis[id] = (montosPorDiocesis[id] ?? 0) + Number(row.monto);
    }
    const diocesisIds = Object.keys(montosPorDiocesis);

    if (diocesisIds.length === 0) return [];

    // Nombres de diócesis
    const { data: diocesisData } = await supabase
      .from('diocesis').select('id, nombre').in('id', diocesisIds);
    const nombresPorId: Record<string, string> = {};
    for (const d of diocesisData || []) nombresPorId[d.id] = d.nombre;

    // Contar lumisiales distintos que enviaron cuota a cada diócesis en el período
    const nLumisialesPorDiocesis: Record<string, number> = {};
    const { data: lumisialesData } = await supabase
      .from('movimientos')
      .select('diocesis_id, origen_id')
      .eq('origen_tipo', 'lumisial')
      .eq('categoria_aporte', 'cuota_iglesia')
      .eq('tipo', 'ingreso')
      .eq('anulado', false)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin)
      .in('diocesis_id', diocesisIds);
    for (const d of diocesisIds) {
      const distintos = new Set(
        (lumisialesData || []).filter(m => m.diocesis_id === d).map(m => m.origen_id)
      );
      nLumisialesPorDiocesis[d] = distintos.size;
    }

    return diocesisIds.map(id => ({
      diocesis_id: id,
      diocesis_nombre: nombresPorId[id] ?? '',
      monto: montosPorDiocesis[id],
      n_lumisiales: nLumisialesPorDiocesis[id] ?? 0,
    }));
  },

  async getCuotaIglesiaDesgloseJANLumisiales(
    diocesisId: string,
    mes: number,
    anio: number
  ): Promise<{ lumisial_id: string; lumisial_nombre: string; monto: number; montoJAL: number; montoPA: number }[]> {
    // Reutiliza la lógica de getCuotaIglesiaDesgloseJAD: movimientos JAD con origen lumisial
    return this.getCuotaIglesiaDesgloseJAD(diocesisId, mes, anio);
  },
};
